import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Avatar, Typography, Chip, IconButton, TextField, Button, Alert, CircularProgress, Tabs, Tab, List, ListItem, ListItemText, ListItemButton, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Badge, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helmet } from '../lib/HelmetShim';
import ProductSection from '../components/ProductSection';
import UploadWidget from '../components/UploadWidget';
import FamilyContent from '../components/FamilyContent';
import { client } from '../api/Api';

const DEFAULT_IMAGE_PATH = "/img/img.png";

const getImageUrl = (imagePath) => {
  if (!imagePath) return (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH;
  return `https://tikrai.com/api/images/${imagePath}`;
};

export default function MyAccount() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editingUsername, setEditingUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const usernameInputRef = useRef(null);
  
  // Initialize activeTab from URL parameter or default to 0
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'messages' ? 1 : tabParam === 'family' ? 2 : 0;
  const [activeTab, setActiveTab] = useState(initialTab); // 0 = Produktai, 1 = Žinutės, 2 = Šeima
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messagesPage, setMessagesPage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [receiptNutrition, setReceiptNutrition] = useState(null);
  const [loadingNutrition, setLoadingNutrition] = useState(false);
  const [receiptItems, setReceiptItems] = useState(null);
  const [loadingReceiptItems, setLoadingReceiptItems] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [meRes, pricesRes] = await Promise.all([
          client.get('/me'),
          client.get('/my-prices'),
        ]);
        if (!mounted) return;
        setMe(meRes.data);
        setPrices(Array.isArray(pricesRes.data) ? pricesRes.data : []);
        loadUnreadCount();
      } catch (e) {
        // Redirect to login page if unauthorized
        if (e.response?.status === 401 || e.response?.status === 403) {
          navigate('/login');
        } else {
          setError('Klaida užkraunant duomenis.');
        }
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    const tabName = activeTab === 1 ? 'messages' : activeTab === 2 ? 'family' : null;
    if (tabName) {
      setSearchParams({ tab: tabName });
    } else {
      setSearchParams({});
    }
  }, [activeTab, setSearchParams]);

  // Load messages when messages tab is active
  useEffect(() => {
    if (activeTab === 1 && messages.length === 0 && !messagesLoading) {
      loadMessages();
    }
  }, [activeTab]);

  const loadUnreadCount = async () => {
    try {
      const res = await client.get('/messages/unread-count');
      setUnreadCount(res.data.unreadCount || 0);
    } catch (e) {
      // Ignore errors
    }
  };

  const loadMessages = async (pageNum = 0) => {
    try {
      if (pageNum === 0) {
        setMessagesLoading(true);
      }
      const res = await client.get('/messages', { params: { page: pageNum, size: 20 } });
      if (pageNum === 0) {
        setMessages(res.data.messages || []);
      } else {
        setMessages(prev => [...prev, ...(res.data.messages || [])]);
      }
      setHasMoreMessages(res.data.hasNext || false);
      setMessagesPage(pageNum);
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida užkraunant žinutes.');
      }
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleMessageClick = async (messageId) => {
    try {
      const res = await client.get(`/messages/${messageId}`);
      setSelectedMessage(res.data);
      
      // Get receipt items and nutrition directly from message response
      if (res.data.receiptItems) {
        setReceiptItems({
          items: res.data.receiptItems,
          storeName: res.data.storeName || '',
          purchaseDate: res.data.purchaseDate || ''
        });
      } else {
        setReceiptItems(null);
      }
      
      if (res.data.receiptNutrition) {
        setReceiptNutrition(res.data.receiptNutrition);
      } else {
        setReceiptNutrition(null);
      }

      // Reload messages to update read status
      loadMessages(messagesPage);
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida užkraunant žinutę.');
      }
    }
  };

  const loadReceiptItems = async (messageId) => {
    setLoadingReceiptItems(true);
    try {
      const res = await client.get(`/messages/${messageId}/receipt-items`);
      setReceiptItems(res.data);
    } catch (e) {
      // Ignore errors - receipt items might not be available
      setReceiptItems(null);
    } finally {
      setLoadingReceiptItems(false);
    }
  };

  const handleReprocessReceipt = async (messageId, e) => {
    e.stopPropagation();
    setReprocessing(true);
    try {
      await client.post(`/messages/${messageId}/reprocess-receipt`);
      // Reload message - receipt items and nutrition will be included
      const res = await client.get(`/messages/${messageId}`);
      setSelectedMessage(res.data);
      
      // Get receipt items and nutrition directly from message response
      if (res.data.receiptItems) {
        setReceiptItems({
          items: res.data.receiptItems,
          storeName: res.data.storeName || '',
          purchaseDate: res.data.purchaseDate || ''
        });
      } else {
        setReceiptItems(null);
      }
      
      if (res.data.receiptNutrition) {
        setReceiptNutrition(res.data.receiptNutrition);
      } else {
        setReceiptNutrition(null);
      }
      // Reload messages list
      await loadMessages(messagesPage);
      alert('Kvitas sėkmingai per\'procesintas!');
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        alert('Klaida per\'procesinant kvitą: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setReprocessing(false);
    }
  };

  const loadReceiptNutrition = async (messageId) => {
    setLoadingNutrition(true);
    try {
      const res = await client.get(`/messages/${messageId}/receipt-nutrition`);
      setReceiptNutrition(res.data);
    } catch (e) {
      // Ignore errors - nutrition might not be available
      setReceiptNutrition(null);
    } finally {
      setLoadingNutrition(false);
    }
  };

  const isReceiptMessage = (message) => {
    if (!message) return false;
    const subject = (message.subject || '').toLowerCase();
    const fromEmail = (message.fromEmail || '').toLowerCase();
    return subject.includes('kvitas') || subject.includes('receipt') ||
        fromEmail.includes('maxima') || fromEmail.includes('iki') ||
        fromEmail.includes('rimi') || fromEmail.includes('norfa');
  };

  const getMessageIcon = (message) => {
    if (!message) return <NotificationsIcon />;
    
    // Use type field to determine icon
    const messageType = message.type;
    if (messageType === 'UPLOAD') {
      return <ReceiptIcon />;
    }
    
    // Default to EmailIcon for EMAIL type or any other type
    return <EmailIcon />;
  };

  const handleDeleteMessage = async (messageId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Ar tikrai norite ištrinti šią žinutę?')) {
      return;
    }
    try {
      await client.delete(`/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida trinant žinutę.');
      }
    }
  };

  const handleMarkAsRead = async (messageId, e) => {
    if (e) e.stopPropagation();
    try {
      await client.post(`/messages/${messageId}/mark-read`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, isRead: true } : m));
      loadUnreadCount();
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Klaida pažymint žinutę kaip perskaitytą.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle ISO format with or without timezone
      let date;
      if (typeof dateString === 'string') {
        // Handle ISO format like "2025-12-28T21:50:50" (without timezone)
        if (dateString.includes('T')) {
          // If no timezone, assume UTC and convert to local
          if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
            date = new Date(dateString + 'Z');
          } else {
            date = new Date(dateString);
          }
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        // Try parsing as LocalDateTime format
        try {
          const parts = dateString.split('T');
          if (parts.length === 2) {
            const datePart = parts[0].split('-');
            const timePart = parts[1].split(':');
            if (datePart.length === 3 && timePart.length >= 2) {
              date = new Date(
                  parseInt(datePart[0]),
                  parseInt(datePart[1]) - 1,
                  parseInt(datePart[2]),
                  parseInt(timePart[0]),
                  parseInt(timePart[1]),
                  timePart.length > 2 ? parseInt(timePart[2]) : 0
              );
            }
          }
        } catch (e2) {
          return '';
        }

        if (isNaN(date.getTime())) {
          return '';
        }
      }

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Dabar';
      if (diffMins < 60) return `Prieš ${diffMins} min.`;
      if (diffHours < 24) return `Prieš ${diffHours} val.`;
      if (diffDays < 7) return `Prieš ${diffDays} d.`;
      return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const getPreview = (textContent, htmlContent) => {
    if (htmlContent) {
      const div = document.createElement('div');
      div.innerHTML = htmlContent;
      const text = div.textContent || div.innerText || '';
      return text.substring(0, 100) + (text.length > 100 ? '...' : '');
    }
    if (textContent) {
      return textContent.substring(0, 100) + (textContent.length > 100 ? '...' : '');
    }
    return 'Nėra turinio';
  };

  const getMessageTitle = (message) => {
    if (isReceiptMessage(message)) {
      // Check if processed and has store name
      if (message.isProcessed === true && message.storeName && message.storeName.trim() !== '') {
        return `Kvitas ${message.storeName}`;
      }
      return 'Kvitas';
    }
    return message.subject || '(Be temos)';
  };

  const formatPurchaseDate = (purchaseDate) => {
    if (!purchaseDate) return '';
    try {
      // Handle various date formats
      let date;

      // If it's a number (timestamp), convert to date
      if (typeof purchaseDate === 'number' || /^\d+$/.test(String(purchaseDate))) {
        const timestamp = typeof purchaseDate === 'number' ? purchaseDate : parseInt(String(purchaseDate));
        // If it's a very large number, might be milliseconds, otherwise seconds
        date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
      } else if (typeof purchaseDate === 'string') {
        // Try common date formats
        // Format: YYYY-MM-DD or YYYYMMDD
        if (/^\d{8}$/.test(purchaseDate)) {
          // YYYYMMDD format
          const year = purchaseDate.substring(0, 4);
          const month = purchaseDate.substring(4, 6);
          const day = purchaseDate.substring(6, 8);
          date = new Date(`${year}-${month}-${day}`);
        } else if (/^\d{4}-\d{2}-\d{2}/.test(purchaseDate)) {
          // YYYY-MM-DD format
          date = new Date(purchaseDate.split(' ')[0]); // Take only date part if there's time
        } else {
          // Try standard Date parsing
          date = new Date(purchaseDate);
        }
      } else {
        date = new Date(purchaseDate);
      }

      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('lt-LT', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }

      // If parsing fails, try to extract date parts manually
      const str = String(purchaseDate);
      if (/^\d{8}$/.test(str)) {
        const year = str.substring(0, 4);
        const month = str.substring(4, 6);
        const day = str.substring(6, 8);
        const monthNames = ['sausio', 'vasario', 'kovo', 'balandžio', 'gegužės', 'birželio',
          'liepos', 'rugpjūčio', 'rugsėjo', 'spalio', 'lapkričio', 'gruodžio'];
        const monthIndex = parseInt(month) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          return `${parseInt(day)} ${monthNames[monthIndex]} ${year}`;
        }
      }

      // If all else fails, return as-is
      return purchaseDate;
    } catch (e) {
      return purchaseDate;
    }
  };

  const stats = useMemo(() => {
    const receiptCount = prices.length;
    const uniqueProducts = new Set(prices.map(p => p?.product?.id).filter(Boolean));
    return {
      receiptCount,
      uniqueProductCount: uniqueProducts.size,
      points: me?.points || 0,
    };
  }, [prices, me]);

  const handleEditUsernameClick = () => {
    setEditingUsername(me?.username || '');
    setUsernameError('');
    setIsEditingUsername(true);
    // Focus input after state update
    setTimeout(() => {
      if (usernameInputRef.current) {
        usernameInputRef.current.focus();
        // Move cursor to end
        const input = usernameInputRef.current.querySelector('input');
        if (input) {
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }
    }, 0);
  };

  const handleCancelEdit = () => {
    setIsEditingUsername(false);
    setEditingUsername('');
    setUsernameError('');
  };

  const handleUsernameChange = (value) => {
    setEditingUsername(value);
    setUsernameError(''); // Clear error when typing
  };

  const handleSaveUsername = async () => {
    if (!editingUsername || editingUsername.trim() === '') {
      setUsernameError('Username negali būti tuščias');
      return;
    }

    if (editingUsername === me?.username) {
      handleCancelEdit();
      return;
    }

    // Check username availability before saving
    setCheckingUsername(true);
    setSavingUsername(true);
    try {
      // First check if username is available
      const checkRes = await client.get('/check-username', { params: { username: editingUsername } });
      if (!checkRes.data.available) {
        setUsernameError('Toks username jau egzistuoja');
        setCheckingUsername(false);
        setSavingUsername(false);
        return;
      }

      // If available, save it
      const res = await client.post('/update-username', { username: editingUsername });
      if (res.data.success) {
        setMe({ ...me, username: res.data.username });
        setIsEditingUsername(false);
        setEditingUsername('');
        setUsernameError('');
      }
    } catch (e) {
      if (e.response?.status === 409) {
        setUsernameError('Toks username jau egzistuoja');
      } else if (e.response?.status === 400) {
        setUsernameError(e.response?.data?.error || 'Netinkamas username formatas');
      } else {
        setUsernameError('Nepavyko išsaugoti username');
      }
    } finally {
      setCheckingUsername(false);
      setSavingUsername(false);
    }
  };

  // Build product cards from user's price history
  const productCards = useMemo(() => {
    // Map productId -> { alias, name, image, lastPrice, previousPrice }
    const byProduct = new Map();
    for (const p of prices) {
      const pr = p?.product;
      if (!pr?.id) continue;
      const key = pr.id;
      const existing = byProduct.get(key);
      const val = p.value;
      if (!existing) {
        byProduct.set(key, {
          alias: pr.alias,
          name: pr.name,
          image: pr.image,
          lastPrice: val,
          previousPrice: undefined,
        });
      } else if (existing.previousPrice === undefined) {
        // First older price seen for this product (list is desc by date)
        if (val !== existing.lastPrice) {
          existing.previousPrice = val;
        }
      }
    }
    return Array.from(byProduct.values());
  }, [prices]);

  if (error) {
    return <div style={{ maxWidth: 800, margin: '20px auto' }}>{error}</div>;
  }
  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, margin: '20px auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
      </Box>
    );
  }

  return (
      <>
        <Helmet>
          <title>Mano paskyra – Tikrai</title>
          <meta name="description" content="Vartotojo paskyros puslapis su statistika ir įkeltų produktų sąrašu." />
        </Helmet>
        <Container maxWidth="xl" className="container" sx={{ px: { xs: 1, sm: 2 } }}>
          {/* Profile header card */}
          <Box className="section" sx={{ mt: { xs: 2, sm: 3 } }}>
            <Box className="card shadow-soft" sx={{ background: 'linear-gradient(135deg, var(--bg-16) 0%, #fff 100%)', border: '1px solid var(--line)', borderRadius: 3, p: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, position: 'relative' }}>
              <IconButton
                  onClick={() => { window.location.href = '/logout'; }}
                  sx={{
                    position: 'absolute',
                    top: { xs: 8, sm: 12 },
                    right: { xs: 8, sm: 12 },
                    color: '#2e7d32',
                    '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
                  }}
                  aria-label="logout"
                  title="Atsijungti"
              >
                <LogoutIcon />
              </IconButton>
              <Avatar src={me?.photoUrl} alt={me?.username || 'user'} sx={{ width: { xs: 64, sm: 84 }, height: { xs: 64, sm: 84 } }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  {isEditingUsername ? (
                      <>
                        <TextField
                            inputRef={usernameInputRef}
                            autoFocus
                            value={editingUsername}
                            onChange={(e) => handleUsernameChange(e.target.value)}
                            error={!!usernameError}
                            helperText={usernameError || (checkingUsername ? 'Tikrinama...' : (savingUsername ? 'Išsaugoma...' : ''))}
                            disabled={checkingUsername || savingUsername}
                            size="small"
                            sx={{
                              width: 'auto',
                              minWidth: 150,
                              maxWidth: '100%',
                              '& .MuiInputBase-root': {
                                fontSize: '1.5rem',
                                fontWeight: 800,
                                width: 'fit-content',
                              },
                              '& .MuiInputBase-input': {
                                width: 'fit-content',
                                minWidth: '10ch',
                                paddingRight: '8px',
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !usernameError && !checkingUsername && !savingUsername) {
                                handleSaveUsername();
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                        />
                        <IconButton
                            onClick={handleSaveUsername}
                            disabled={!!usernameError || checkingUsername || savingUsername || editingUsername === me?.username}
                            color="primary"
                            sx={{
                              backgroundColor: 'success.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'success.dark' },
                              '&:disabled': { backgroundColor: 'action.disabledBackground' }
                            }}
                        >
                          {savingUsername ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                        </IconButton>
                        <IconButton
                            onClick={handleCancelEdit}
                            disabled={savingUsername}
                            color="error"
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                  ) : (
                      <>
                        <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              cursor: 'pointer',
                              '&:hover': { color: 'primary.main' }
                            }}
                            onClick={handleEditUsernameClick}
                        >
                          {me?.username || 'Vartotojas'}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleEditUsernameClick}
                            sx={{ color: 'text.secondary' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {me?.email || ''}{me?.email && me?.phone ? ' • ' : ''}{me?.phone || ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                  <Chip label={`Taškai: ${stats.points}`} color="success" variant="filled" size="small" />
                  <Chip label={`Produktai: ${stats.uniqueProductCount}`} variant="outlined" size="small" />
                  <Chip label={`Čekiai/kainos: ${stats.receiptCount}`} variant="outlined" size="small" />
                  <Chip
                      icon={<EmailIcon />}
                      label={`Žinutės${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
                      variant={unreadCount > 0 ? "filled" : "outlined"}
                      color={unreadCount > 0 ? "error" : "default"}
                      size="small"
                      onClick={() => setActiveTab(1)}
                      sx={{ cursor: 'pointer' }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Tabs navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Mano prekės" />
              <Tab
                  label={
                    <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error">
                      Žinutės
                    </Badge>
                  }
              />
              <Tab label="Šeima" />
            </Tabs>
          </Box>

          {/* Tab content */}
          {activeTab === 0 && (
              <>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mt: { xs: 2, sm: 3 }, mb: 1 }}>Mano prekės</Typography>
                <ProductSection
                    products={productCards}
                    isLoading={false}
                    isLoadingMore={false}
                    hasMoreProducts={false}
                    totalElements={productCards.length}
                    loadMoreProducts={() => {}}
                />
              </>
          )}

          {activeTab === 1 && (
              <Box sx={{ mt: 2 }}>
                {messagesLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={48} />
                    </Box>
                ) : messages.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Nėra gautų žinučių
                      </Typography>
                    </Paper>
                ) : (
                    <>
                      <Paper>
                        <List sx={{ p: 0 }}>
                          {messages.map((message) => (
                              <ListItem
                                  key={message.id}
                                  disablePadding
                                  sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: message.isRead ? 'transparent' : 'action.hover',
                                    '&:hover': { backgroundColor: 'action.selected' }
                                  }}
                              >
                                <ListItemButton onClick={() => handleMessageClick(message.id)}>
                                  <Avatar
                                      sx={{
                                        bgcolor: message.type === 'UPLOAD' ? 'primary.main' : 'secondary.main',
                                        width: 40,
                                        height: 40,
                                        mr: 2
                                      }}
                                  >
                                    {getMessageIcon(message)}
                                  </Avatar>
                                  <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                          <Typography
                                              variant="subtitle1"
                                              sx={{
                                                fontWeight: message.isRead ? 400 : 700,
                                                flex: 1
                                              }}
                                          >
                                            {getMessageTitle(message)}
                                          </Typography>
                                          {!message.isRead && (
                                              <Chip label="Nauja" color="primary" size="small" />
                                          )}
                                          {isReceiptMessage(message) && (
                                              <>
                                                <Chip
                                                    icon={message.isProcessed ? <CheckCircleIcon /> : <PendingIcon />}
                                                    label={message.isProcessed ? 'Apdorotas' : 'Laukia'}
                                                    color={message.isProcessed ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleReprocessReceipt(message.id, e)}
                                                    title="Per'procesinti kvitą"
                                                    disabled={reprocessing}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                  <RefreshIcon fontSize="small" />
                                                </IconButton>
                                              </>
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Typography variant="body2" color="text.secondary">
                                          {isReceiptMessage(message) && message.purchaseDate && message.purchaseDate.trim() !== '' ? (
                                              formatPurchaseDate(message.purchaseDate)
                                          ) : (
                                              formatDate(message.receivedAt)
                                          )}
                                        </Typography>
                                      }
                                  />
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {!message.isRead && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMarkAsRead(message.id, e)}
                                            title="Pažymėti kaip perskaitytą"
                                        >
                                          <MarkEmailReadIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDeleteMessage(message.id, e)}
                                        color="error"
                                        title="Ištrinti"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </ListItemButton>
                              </ListItem>
                          ))}
                        </List>
                      </Paper>

                      {hasMoreMessages && (
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => loadMessages(messagesPage + 1)}
                                disabled={messagesLoading}
                            >
                              {messagesLoading ? <CircularProgress size={20} /> : 'Įkelti daugiau'}
                            </Button>
                          </Box>
                      )}
                    </>
                )}
              </Box>
          )}

          {activeTab === 2 && (
              <Box sx={{ mt: 2 }}>
                <FamilyContent />
              </Box>
          )}

          {/* Message detail dialog */}
          <Dialog
              open={!!selectedMessage}
              onClose={() => setSelectedMessage(null)}
              maxWidth="md"
              fullWidth
          >
            {selectedMessage && (
                <>
                  <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6">{getMessageTitle(selectedMessage)}</Typography>
                      <IconButton
                          size="small"
                          onClick={() => setSelectedMessage(null)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </DialogTitle>
                  <DialogContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Data:</strong> {isReceiptMessage(selectedMessage) && selectedMessage.purchaseDate && selectedMessage.purchaseDate.trim() !== '' ? (
                          formatPurchaseDate(selectedMessage.purchaseDate)
                      ) : selectedMessage.receivedAt ? (
                          formatDate(selectedMessage.receivedAt)
                      ) : ''}
                      </Typography>
                    </Box>

                    {isReceiptMessage(selectedMessage) && selectedMessage.isProcessed && (
                        <>
                          {receiptItems && (
                              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Kvito produktai</Typography>
                                {receiptItems.storeName && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      <strong>Parduotuvė:</strong> {receiptItems.storeName}
                                      {receiptItems.storeAddress && ` (${receiptItems.storeAddress})`}
                                    </Typography>
                                )}
                                {receiptItems.purchaseDate && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                      <strong>Data:</strong> {formatPurchaseDate(receiptItems.purchaseDate)}
                                    </Typography>
                                )}
                                {loadingReceiptItems ? (
                                    <CircularProgress size={24} />
                                ) : receiptItems.items && receiptItems.items.length > 0 ? (
                                    <List>
                                      {receiptItems.items.map((item, index) => (
                                          <ListItem key={index} sx={{ px: 0, py: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                              <Box
                                                  component="img"
                                                  src={item.productImage ? `${getImageUrl(item.productImage)}.png` : (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH}
                                                  alt={item.name || item.nameEN || 'Produktas'}
                                                  onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = (process.env.PUBLIC_URL || '') + DEFAULT_IMAGE_PATH;
                                                  }}
                                                  sx={{
                                                    width: 60,
                                                    height: 60,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    backgroundColor: 'grey.200',
                                                    flexShrink: 0
                                                  }}
                                              />
                                              <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                  {item.name || item.nameEN}
                                                </Typography>
                                                {item.productAlias && (
                                                    <Typography variant="caption" color="text.secondary">
                                                      {item.productAlias}
                                                    </Typography>
                                                )}
                                              </Box>
                                              <Box sx={{ textAlign: 'right' }}>
                                                {item.originalPrice && item.originalPrice !== item.savedPrice ? (
                                                    <>
                                                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                                          {item.originalPrice} €
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                                                          {item.savedPrice} €
                                                        </Typography>
                                                    </>
                                                ) : item.savedPrice ? (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                      {item.savedPrice} €
                                                    </Typography>
                                                ) : item.price ? (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                      {item.price} €
                                                    </Typography>
                                                ) : null}
                                              </Box>
                                            </Box>
                                          </ListItem>
                                      ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Produktų informacija neprieinama
                                    </Typography>
                                )}
                              </Box>
                          )}
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Maistinė vertė</Typography>
                            {loadingNutrition ? (
                                <CircularProgress size={24} />
                            ) : receiptNutrition ? (
                                receiptNutrition.note ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {receiptNutrition.note}
                                    </Typography>
                                ) : (
                                    <Grid container spacing={2}>
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="body2" color="text.secondary">Kalorijos</Typography>
                                        <Typography variant="h6" color="primary">{receiptNutrition.energy_kcal} kcal</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="body2" color="text.secondary">Baltymai</Typography>
                                        <Typography variant="h6">{receiptNutrition.protein_g} g</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="body2" color="text.secondary">Riebalai</Typography>
                                        <Typography variant="h6">{receiptNutrition.fat_g} g</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography variant="body2" color="text.secondary">Angliavandeniai</Typography>
                                        <Typography variant="h6">{receiptNutrition.carbs_g} g</Typography>
                                      </Grid>
                                    </Grid>
                                )
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Maistinės vertės informacija neprieinama
                                </Typography>
                            )}
                          </Box>
                        </>
                    )}

                    {isReceiptMessage(selectedMessage) && selectedMessage.isProcessed ? (
                        <Accordion sx={{ mt: 2 }}>
                          <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="email-content-panel"
                              id="email-content-header"
                          >
                            <Typography variant="subtitle2" color="text.secondary">
                              Laiško turinys
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {selectedMessage.htmlContent ? (
                                <Box
                                    sx={{
                                      '& img': { maxWidth: '100%', height: 'auto' },
                                      '& table': { width: '100%', borderCollapse: 'collapse', margin: '0 0' },
                                      '& table td, & table th': { padding: '8px', border: '1px solid #ddd' },
                                      '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                                      '& *': { maxWidth: '100%' },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: selectedMessage.htmlContent }}
                                />
                            ) : (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {selectedMessage.textContent || 'Nėra turinio'}
                                </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                    ) : (
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                          {selectedMessage.htmlContent ? (
                              <Box
                                  sx={{
                                    '& img': { maxWidth: '100%', height: 'auto' },
                                    '& table': { width: '100%', borderCollapse: 'collapse', margin: '16px 0' },
                                    '& table td, & table th': { padding: '8px', border: '1px solid #ddd' },
                                    '& pre': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
                                    '& *': { maxWidth: '100%' },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: selectedMessage.htmlContent }}
                              />
                          ) : (
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {selectedMessage.textContent || 'Nėra turinio'}
                              </Typography>
                          )}
                        </Box>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSelectedMessage(null)}>Uždaryti</Button>
                    {!selectedMessage.isRead && (
                        <Button
                            onClick={() => handleMarkAsRead(selectedMessage.id, { stopPropagation: () => {} })}
                            startIcon={<MarkEmailReadIcon />}
                        >
                          Pažymėti kaip perskaitytą
                        </Button>
                    )}
                    <Button
                        onClick={() => handleDeleteMessage(selectedMessage.id, { stopPropagation: () => {} })}
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                      Ištrinti
                    </Button>
                  </DialogActions>
                </>
            )}
          </Dialog>
        </Container>

        <UploadWidget />

        {/* Footer same as Home */}
        <Box component="footer" sx={{ borderTop: '1px solid #eee', mt: 6, py: 3, backgroundColor: '#fafafa' }}>
          <Container maxWidth="lg" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <a href="/api/taisykles.html" style={{ color: '#555', textDecoration: 'none' }}>Taisyklės</a>
            <span style={{ color: '#bbb' }}>•</span>
            <a href="/api/privacy-policy.html" style={{ color: '#555', textDecoration: 'none' }}>Privatumo politika</a>
            <span style={{ color: '#bbb' }}>•</span>
            <a href="/api/data-deletion.html" style={{ color: '#555', textDecoration: 'none' }}>Duomenų ištrynimas</a>
          </Container>
        </Box>
      </>
  );
}
